import { useFpB2bButlerItemsService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-butler-items-service'
import { parseAttributesToArray } from '@cart-services/parser/parseAttributes'
import { type ButlerItem } from '@cart-services/types'
import { CartSalePrice } from '@cart-ui/CartSalePrice/CartSalePrice'
import { type WpCartMobileMediaQuery } from '@cart-ui/WpCartLayout/useWpCartResponsive'
import { type CountryCode, type ProjectCode } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiButlerProductRecommendations } from '@hooks-api/fortune/useApiButlerProductRecommendations'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { type Nullable } from '@hooks-api/types/nullable'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { type CheckboxProps, Checkbox } from '@pets-ui/Checkbox/Checkbox'
import { Chip } from '@pets-ui/Chip/Chip'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { HStack } from '@pets-ui/HStack/HStack'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsiveCustom } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { parseToNumber } from '@utils/parseToNumber'
import { useMemo, type MouseEventHandler } from 'react'
import { useButler } from '../ButlerProvider/useButler'
import { DIALOG_CHANGE_BUTLER_ITEM, type DialogChangeButlerItemProps } from '../DialogChangeButlerItem/DialogChangeButlerItem'
import { ButlerItemAlert } from './ButlerItemAlert'
import { ButlerItemImage } from './ButlerItemImage'
import { ButlerListItemLayout } from './ButlerListItemLayout'
import { ButlerListItemStock } from './ButlerListItemStock'
import { IconButtonDeleteItem } from './IconButtonDeleteItem'

const MOBILE: WpCartMobileMediaQuery = '@media (max-width: 983px)'
const MAX_QUANTITY = 99999
const ERROR_RETRY_COUNT = 100
const ERROR_RETRY_INTERVAL = 10000

const styles = stylex.create({
  numberInputButton: {
    height: '24px',
    width: '24px',
  },
  numberInputWidth: {
    backgroundColor: colors.white,
    width: '42px',
  },
  flexibleText: {
    flex: '1',
    overflow: 'hidden',
  },
  containerBox: {
    overflow: 'hidden',
  },
  chip: {
    backgroundColor: colors.violet100,
    color: colors.violet600,
  },
})

export interface ButlerListItemProps {
  item: ButlerItem
  product?: ECommerceProduct
  orderId: string | null
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignCode: string
  campaignId: number

  productTitle?: string
  productImgSrc?: string
  salePrice?: Nullable<string | number>
  maxQuantity?: number

  currency?: string
  onQuantityChange?: ((value?: number) => void)
  onCheckboxChange?: CheckboxProps['onChange']
  onDelete?: MouseEventHandler<HTMLButtonElement>
  onDeleteRecover?: MouseEventHandler<HTMLButtonElement>
  onExchangeButlerItem?: DialogChangeButlerItemProps['onExchangeProduct']
}

export const ButlerListItem = (props: ButlerListItemProps) => {
  const {
    item,
    product,
    orderId,
    projectCode,
    countryCode,
    campaignCode,
    campaignId,

    productTitle,
    productImgSrc,
    salePrice,

    currency,
    onQuantityChange,
    onCheckboxChange,
    onDelete,
    onDeleteRecover,
    onExchangeButlerItem,
  } = props
  const { openDialog } = useDialog<DialogChangeButlerItemProps>(DIALOG_CHANGE_BUTLER_ITEM)

  const { platform } = useButler()
  const { upsertButlerItem } = useFpB2bButlerItemsService(campaignId, platform)
  const recommendationParams = useMemo(() => {
    if (item.recommendations?.length) {
      return undefined
    }

    return {
      order_id: orderId,
      project_code: projectCode,
      country_code: countryCode,
      code: campaignCode,
      draft_name: item.draftName,
      order_item_id: item.orderItemId,
    }
  }, [
    item.recommendations?.length,
    orderId,
    projectCode,
    countryCode,
    campaignCode,
    item.draftName,
    item.orderItemId,
  ])

  useApiButlerProductRecommendations(recommendationParams, {
    onErrorRetry: (_error, _key, _config, revalidate, { retryCount }) => {
      if (retryCount >= ERROR_RETRY_COUNT) return

      setTimeout(() => {
        revalidate({ retryCount: retryCount + 1 })
      }, ERROR_RETRY_INTERVAL)
    },

    onSuccess: async (res) => {
      if (item.orderItemId) {
        const recommendations = res.data.data.recommendations
        const defaultRecommendation = recommendations[0]
        if (!defaultRecommendation) return
        upsertButlerItem({
          cartItemId: item.orderItemId,
          variation_id: defaultRecommendation.wp_variation_id,
          parent_product_id: defaultRecommendation.wp_product_id,
          sku: defaultRecommendation.sku,
          project_code: defaultRecommendation.project_code,
          quantity: defaultRecommendation.quantity,
          stock: defaultRecommendation.stock,
          recommendations,
        })
      }
    },
  })

  const handleQuantityChange: ButlerListItemProps['onQuantityChange'] = (value) => {
    if (value === undefined) return
    onQuantityChange?.(value)
  }

  const isMobile = useResponsiveCustom(MOBILE)
  const attributes = product?.attributes ? parseAttributesToArray(product.attributes) : []
  const stock = Math.max(parseToNumber(item?.stock), 0)
  const salePriceNumber = parseToNumber(salePrice)
  const isDeleted = item.isDeleted
  const outOfStock = item.quantity && item.quantity > stock
  const isRecommending = !item.recommendations?.length || !productTitle

  if (isDeleted) {
    return (
      <ButlerItemAlert
        action={<ButtonBase onClick={onDeleteRecover}>還原</ButtonBase>}
      >
        已刪除
        {' '}
        {productTitle}
      </ButlerItemAlert>
    )
  }

  return (
    <>
      {isRecommending
        ? (
          <Box display="flex" justifyContent="center" alignItems="center" backgroundColor={colors.white} gap={1} marginRight={isMobile ? 4 : 0} marginLeft={isMobile ? 4 : 0} height={isMobile ? '190px' : '104px'} borderRadius={8}>
            <CircularProgress color={colors.blue600} fontSize={16} />
            <Text variant="caption" color={colors.grey500}>AI 辨識中...</Text>
          </Box>
        )
        : (
          <VStack backgroundColor={outOfStock ? colors.red50 : colors.white} p={3} gap={3} borderRadius={8} marginRight={isMobile ? 4 : 0} marginLeft={isMobile ? 4 : 0}>
            <ButlerListItemLayout alignItems={isMobile ? 'flex-start' : 'center'}>
              <HStack gap="20px" alignItems="center" height="100%">
                <Checkbox color="blue" checked={item.checked} onChange={onCheckboxChange} disabled={!!outOfStock} />
                <Text width="18px">{item.itemIndex}</Text>
                {isMobile
                  ? (
                    <HStack gap={2} padding={2} flex={1} height="100%" flexWrap="nowrap" backgroundColor={colors.grey100} borderRadius={4} rootStyle={styles.containerBox}>
                      <Text>{item.barcode}</Text>
                      <Text ellipsis rootStyle={styles.flexibleText}>{item.draftName}</Text>
                    </HStack>
                  )
                  : (
                    <VStack gap={2} padding={2} marginRight={1} height="100%" width="160px" justifyContent="center" backgroundColor={colors.grey100} borderRadius={4}>
                      <Text>{item.barcode}</Text>
                      <Text>{item.draftName}</Text>
                    </VStack>
                  )}
              </HStack>
              <ButlerItemImage
                productImgSrc={productImgSrc}
                productTitle={productTitle}
                showAiIcon={true}
              />
              <VStack gap={2}>
                <Text
                  variant="subtitle"
                  {...(isMobile ? { noWrap: true, ellipsis: true, width: 200 } : { lineClamp: 2, maxWidth: 360 })}
                >
                  {productTitle}
                </Text>
                <HStack gap={1}>
                  {attributes.map((attribute, index) => <Chip key={index} rootStyle={styles.chip} borderRadius={4}>{attribute}</Chip>)}
                </HStack>
              </VStack>
              {isMobile
                ? (
                  <HStack gap={2} justifyContent="space-between" width="100%" alignItems="center">
                    <>
                      <HStack gap={1} alignItems="flex-end">
                        <CartSalePrice currency={currency} salePrice={salePriceNumber} quantity={item.quantity} priceType="totalPrice" />
                        <CartSalePrice currency={currency} salePrice={salePriceNumber} quantity={item.quantity} priceType="salePrice" />
                      </HStack>
                      <VStack alignItems="center">
                        <NumberInput
                          value={item.quantity}
                          min={stock > 0 ? 1 : 0}
                          max={MAX_QUANTITY}
                          onChange={handleQuantityChange}
                          autoSelect
                          ButtonDecrementProps={{
                            rootStyle: styles.numberInputButton,
                          }}
                          ButtonIncrementProps={{
                            rootStyle: styles.numberInputButton,
                          }}
                          rootStyle={styles.numberInputWidth}
                        />
                        <ButlerListItemStock stock={stock} quantity={item.quantity} />
                      </VStack>
                    </>
                  </HStack>
                )
                : (
                  <HStack gap={2} justifyContent="space-between" width="100%" alignItems="center" minWidth={240} marginLeft={3}>
                    <>
                      <CartSalePrice currency={currency} salePrice={salePriceNumber} quantity={item.quantity} priceType="salePrice" />
                      <VStack alignItems="center">
                        <NumberInput
                          value={item.quantity}
                          min={stock > 0 ? 1 : 0}
                          max={MAX_QUANTITY}
                          onChange={handleQuantityChange}
                          autoSelect
                          ButtonDecrementProps={{
                            rootStyle: styles.numberInputButton,
                          }}
                          ButtonIncrementProps={{
                            rootStyle: styles.numberInputButton,
                          }}
                          rootStyle={styles.numberInputWidth}
                        />
                        <ButlerListItemStock stock={stock} quantity={item.quantity} />
                      </VStack>
                      <HStack minWidth={50} justifyContent="flex-end">
                        <CartSalePrice currency={currency} salePrice={salePriceNumber} quantity={item.quantity} priceType="totalPrice" />
                      </HStack>
                    </>
                  </HStack>
                )}
              <Button color="grey" borderColor="grey" variant="outlined" width="52px" height={isMobile ? '28px' : undefined} marginLeft={isMobile ? 0 : 5} onClick={() => openDialog({ recommendations: item.recommendations, variationId: item.variation_id, onExchangeProduct: onExchangeButlerItem })}>更換</Button>
              <IconButtonDeleteItem onClick={onDelete} />
            </ButlerListItemLayout>
          </VStack>
        )}
    </>
  )
}
